import { Box, Button } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import { useEffect } from "react"
import { AuditModal } from "routes/regime/components/audit-modal"
import { AcceptedChanged } from "event-definitions"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { PAYLOAD_STATUS_REJECTED } from "routes/audit/lib/audit-constants.mjs"
import { useReviewDecisions } from "./review-decisions"

export function InlineRejectButton() {
    const { buttonId, accepted, uniqueId, auditPayload, referenceId, userName, setOptimisticDecision, ensureAuditId } =
        useBoundContext()
    const decisions = useReviewDecisions()
    const taskId = auditPayload?.taskId
    const rejected = accepted === false
    const reasonText = useDialog(
        <AuditModal
            title="Justify Rejection"
            label="Justification"
            field="reason"
            placeholder="Enter justification"
            okCaption="Reject Change"
        />
    )

    useEffect(() => {
        AcceptedChanged.raise({ id: buttonId, taskId, accepted, uniqueId })
    }, [uniqueId, accepted, buttonId, taskId])

    return (
        <Box display="flex" alignItems="center">
            <Button
                size="small"
                variant={accepted ? "outlined" : undefined}
                startIcon={rejected && <Iconify icon="mdi:close" />}
                onClick={rejected ? showRejected : reject}
                sx={{
                    mr: 2,
                    color: "error.main",
                    borderColor: "error.light",
                    "&:hover": { borderColor: "error.main", backgroundColor: "error.lighter" },
                }}
                data-cy={`inline-reject-button-${buttonId}`}
            >
                {rejected ? "Rejected" : "Reject"}
            </Button>
        </Box>
    )

    async function reject() {
        const text = await reasonText()
        if (text) {
            const currentAuditId = ensureAuditId()
            const actionedOn = new Date().toISOString()
            auditPayload.shortDescription = "Change Rejected"
            auditPayload.status = PAYLOAD_STATUS_REJECTED
            auditPayload.event = "tailoring!custom-schedule-reject"
            auditPayload.reason = text.reason
            auditPayload.actionedOn = actionedOn
            auditPayload.userName = userName

            // Optimistic: button + red justification box reflect this immediately (including the
            // reason just entered); clear it to revert on failure.
            setOptimisticDecision({ status: PAYLOAD_STATUS_REJECTED, reason: text.reason, userName, actionedOn })
            try {
                await decisions.record({
                    auditId: currentAuditId,
                    referenceId,
                    shortDescription: "Change Rejected",
                    payload: auditPayload,
                })
            } catch (e) {
                console.warn("Decision update failed (offline or network error):", e?.message || e)
                setOptimisticDecision(null)
                errorSnackbar("Failed to save rejection. Please try again.")
            }
        }
    }
    function showRejected() {
        return infoSnackbar("Change already rejected.")
    }
}
